import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Keeps a scroll container pinned to the newest content at the bottom — the
 * chat/thread behaviour used by the inbox modals and the fish/member details
 * messages tab.
 *
 * Attach the returned ref to the scrollable element. Pass `dep` a value that
 * changes whenever the content changes (e.g. the messages array or its length)
 * so the hook can re-pin as messages arrive. Pass `resetKey` a value that
 * changes when the conversation is swapped (e.g. the selected inbox/thread id)
 * to force an immediate jump to the bottom regardless of scroll position.
 *
 * If the user scrolls up to read history the hook stops pinning until they
 * return near the bottom, so it never yanks them away mid-read.
 */
export function useStickToBottom(
  dep: unknown,
  resetKey?: unknown,
): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  // Start pinned so the first render lands at the bottom.
  const pinnedRef = useRef(true);

  // Track whether the user is currently near the bottom of the container.
  // Re-attached on resetKey because the scroll element can mount with the
  // conversation.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      pinnedRef.current = distanceFromBottom < 40;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [resetKey]);

  // Swapping conversation always jumps to the bottom.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    pinnedRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [resetKey]);

  // New content re-pins to the bottom only while the user is already there.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [dep]);

  return ref;
}

export default useStickToBottom;
